import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { RoleRefresh } from 'src/app/components/catalogs/roles/store/role.action';
import { Role } from 'src/app/components/catalogs/roles/services/role';
import { Kind, RSF, RSFAmendment, RSFHoldPoint } from 'src/app/components/rsf/rsf';
import { SCFHoldPointResource, SCFMaster } from 'src/app/components/scf-v2/scf';
import { WfTaskLocalResource } from 'src/app/components/workflow/workflow';
import { utils } from 'src/app/modules/libs/utils';

export type HoldpointDialogData =
  | { formType: 'SCF'; form: SCFMaster; holdPoint: SCFHoldPointResource }
  | { formType: 'RSF'; form: RSF; formProxy: RSF | RSFAmendment; holdPoint: RSFHoldPoint };

@Component({
  selector: 'holdpoint-dialog',
  templateUrl: './holdpoint-dialog.component.html',
  styleUrls: ['./holdpoint-dialog.component.scss']
})
export class HoldpointDialogComponent implements OnInit, OnDestroy {
  wfTaskLocals?: WfTaskLocalResource[];
  roles?: Role[];
  roles$!: Observable<Role[]>;
  rolesSubs!: Subscription;

  constructor(
    private matDialogRef: MatDialogRef<HoldpointDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: HoldpointDialogData,
    private store: Store
  ) { }

  /**
   * Loads role metadata and initializes the inspection choices for the selected form type.
   */
  ngOnInit(): void {
    this.loadRoles();
    if (this.data.holdPoint.wfTaskLocals) {
      this.wfTaskLocals = this.data.holdPoint.wfTaskLocals.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      this.wfTaskLocals.forEach(task => task.required = task.wfTasks?.length ? task.wfTasks[0].required : false);
      return;
    }

    const sectionNumber = this.data.formType === 'SCF' ? 3 : 4;
    const tasks = this.data.form.wfTable?.wfTableLocal?.wfSectionLocals.find(x => x.number === sectionNumber)
      ?.wfStepLocals?.find(x => x.code === 'HOLDPOINT')?.wfTaskLocals;
    // Exclude workflow tasks already assigned to either a base-form or amendment hold point.
    this.wfTaskLocals = utils.cloneDeep(tasks?.filter(x => this.isAvailableTask(x))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
  }

  /**
   * Releases the role-store subscription when the dialog closes.
   */
  ngOnDestroy(): void {
    this.rolesSubs?.unsubscribe();
  }

  /**
   * Returns the form serial number for the shared comments editor.
   */
  get serialNo(): string | undefined {
    return this.data.formType === 'SCF' ? this.data.form.serialNo : this.data.formProxy.serialNo;
  }

  /**
   * Returns the role codes supplied with the workflow task, with the role catalog as a fallback.
   * @param wfTaskLocal Inspection workflow task whose permitted roles should be displayed.
   */
  getRoles(wfTaskLocal: WfTaskLocalResource): string | null {
    if (wfTaskLocal.requesterSignature) return null;

    return wfTaskLocal.wfRoleLocals?.map(roleLocal =>
      roleLocal.role ?? this.roles?.find(role => role.id === roleLocal.roleID))
      .filter((role): role is Role => !!role)
      .map(role => role.code)
      .join('/') ?? '';
  }

  /**
   * Returns the requester name for requester-specific inspection chips.
   * @param wfTaskLocal Inspection workflow task being displayed.
   */
  getUserName(wfTaskLocal: WfTaskLocalResource): string | null {
    if (!wfTaskLocal.requesterSignature) return null;
    return this.data.formType === 'SCF'
      ? this.data.form.requesterName ?? '[Requester Name]'
      : this.data.formProxy.requester?.name ?? '[Requester Name]';
  }

  /**
   * Updates the hold-point description from the shared comments editor.
   * @param value Rich-text value emitted by the editor.
   */
  textChanged(value: string): void {
    this.data.holdPoint.description = value;
  }

  /**
   * Closes the dialog without returning a hold point.
   */
  cancel(): void {
    this.matDialogRef.close();
  }

  /**
   * Applies form-specific parent identifiers and closes with a valid hold point.
   */
  save(): void {
    const parentID = this.getParentID();
    if (!this.isValid() || !parentID) return;

    if (!this.data.holdPoint.id)
      this.resetWorkflowIDs();

    this.data.holdPoint.wfTaskLocals = this.wfTaskLocals ?? [];
    if (this.data.formType === 'SCF') {
      // SCF amendments retain their amendment number in addition to the parent identifier.
      const amendment = this.data.form.scfAmendments?.find(item => item.active);
      this.data.holdPoint.scfMasterID = parentID;
      this.data.holdPoint.amendmentID = amendment?.id;
      this.data.holdPoint.amendmentNumber = amendment?.number;
    } else {
      this.data.holdPoint.rsfid = this.data.formProxy.kind === Kind.RSF ? parentID : 0;
      this.data.holdPoint.rsfAmendmentID = this.data.formProxy.kind === Kind.RSFAmendment ? parentID : 0;
    }
    this.matDialogRef.close(this.data.holdPoint);
  }

  /**
   * Determines whether the hold point has visible text and at least one required inspection.
   */
  isValid(): boolean {
    return this.hasDescription() && !!this.wfTaskLocals?.some(x => x.required);
  }

  /**
   * Checks rendered rich text so empty editor markup does not count as a description.
   */
  hasDescription(): boolean {
    const document = new DOMParser().parseFromString(this.data.holdPoint.description ?? '', 'text/html');
    return (document.body.textContent ?? '').replace(/\u00A0/g, ' ').trim().length > 0;
  }

  /**
   * Loads role catalog entries used when workflow responses do not embed role details.
   */
  private loadRoles(): void {
    this.roles$ = this.store.select(state => state.Roles.data);
    this.rolesSubs = this.roles$.subscribe(data => this.roles = data);
    if (!this.store.selectSnapshot(state => state.Roles.data)?.length)
      this.store.dispatch(new RoleRefresh());
  }

  /**
   * Determines whether a workflow task is free for a new hold point.
   * @param task Workflow task being considered.
   */
  private isAvailableTask(task: WfTaskLocalResource): boolean {
    return this.data.formType === 'SCF'
      ? !task.scfHoldPointID
      : !task.rsfHoldPointID && !task.rsfAmendmentHoldPointID;
  }

  /**
   * Returns the active form or amendment identifier required before saving.
   */
  private getParentID(): number | undefined {
    return this.data.formType === 'SCF' ? this.data.form.id : this.data.formProxy.id;
  }

  /**
   * Clears cloned workflow identifiers before a new hold point is persisted.
   */
  private resetWorkflowIDs(): void {
    this.wfTaskLocals?.forEach(task => {
      task.id = 0;
      task.wfActionLocals?.forEach(action => action.id = 0);
      task.wfRoleLocals?.forEach(role => role.id = 0);
      task.wfUserLocals?.forEach(user => {
        user.id = 0;
        user.user = null;
      });
    });
  }
}
